import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  InputLabel,
  ListSubheader,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Autocomplete,
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import { isStudioScopedRole, isManagerOrAbove } from '../utils/roles';
import { useMyLocations, branchLabel, locationsWithRole } from '../hooks/useMyLocations';
import { LocationChecks, useBrandLocations, useCreateAtLocations, createAtEach, partialResultMessage, matchClass, idOf as idOfLoc } from './multiLocation/shared';

interface Capability {
  title: string;
  image: string;
}

interface PackFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** `note` = a short outcome line to show after the dialog closes (e.g. guest booking applied to holders). */
  onSuccess: (note?: string) => void;
  pack?: any;
  /** Prefills and locks the pack to this studio (e.g. from studio profile Packs tab). */
  defaultStudioId?: string;
  defaultStudioName?: string;
}

const sessionTypeLabel = (t: string | null | undefined) =>
  t === 'private' ? 'Private' : t === 'duet' ? 'Duet' : t === 'group' ? 'Group' : 'All session types';

const PackFormDialog: React.FC<PackFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  pack,
  defaultStudioId,
  defaultStudioName,
}) => {
  const isEditMode = !!pack;
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  // Guest booking per pack: platform staff, owners and managers — not instructors.
  const canEditGuestBooking = !isStudioRole || isManagerOrAbove(admin?.role);
  const lockToStudio = Boolean(defaultStudioId) && !isEditMode;

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    totalClasses: '',
    totalPrice: '',
    capabilities: [] as Capability[],
    category: '',
    packType: 'global', // 'global' or 'studio'
    studio: '',
    durationMonths: '',
    durationDays: '',
    sessionType: 'group' as 'group' | 'private' | 'duet', // default group
    freezeAllowed: true, // whether users can freeze a pack bought from this template
    isPrivate: false, // assign-only: hidden from the app, granted via Assign Pack
    shareAcrossBrand: false, // usable at every location of the brand
    allowGuestBooking: false, // holders can book friends onto a class from this pack
  });
  // Purchases per member (Pack.maxPurchasesPerUser): unlimited / once / up to N.
  // A member who reaches it no longer sees the pack in the app.
  const [purchaseLimitMode, setPurchaseLimitMode] = useState<'unlimited' | 'limited'>('unlimited');
  const [purchaseLimitN, setPurchaseLimitN] = useState('1');
  // Pack-level freeze days (Pack.freezeDays): blank = studio freeze policy.
  const [freezeDays, setFreezeDays] = useState('');

  // Combo pack (optional, studio packs only): split totalClasses across categories
  // OR across specific classes (single dimension per pack). A row's ref is a
  // ClassType id / Class id (per comboMode), or the sentinel 'wellness'.
  const [comboEnabled, setComboEnabled] = useState(false);
  const [comboMode, setComboMode] = useState<'category' | 'class'>('category');
  const [comboQuotas, setComboQuotas] = useState<{ category: string; count: string }[]>([]);
  // How a multi-category pack is counted:
  //   'separate' → a count per category/class (combo: Pilates 10 + Yoga 10);
  //   'shared'   → ONE Total Classes usable across all of them in any mix
  //                (shared pack: Pack.allowedCategories / allowedClasses).
  const [comboCounting, setComboCounting] = useState<'separate' | 'shared'>('separate');
  const comboShared = comboEnabled && comboCounting === 'shared';
  // Scope of a NORMAL (non-combo) pack: 'all' | 'category' | 'class', then one
  // category / class (Pack.category / Pack.class). Several categories sharing one
  // count live in the combo section ("One shared total").
  const [normalScope, setNormalScope] = useState<'all' | 'category' | 'class'>('all');
  const [scopeCategories, setScopeCategories] = useState<string[]>([]);
  const [scopeClasses, setScopeClasses] = useState<string[]>([]);
  const applyScope = (scope: 'all' | 'category' | 'class') => {
    setNormalScope(scope);
    if (scope !== 'category') setScopeCategories([]);
    if (scope !== 'class') setScopeClasses([]);
  };
  const idOf = (v: any): string => (v?._id ?? v ?? '').toString();
  const isSharedScope = comboShared;

  const [classTypes, setClassTypes] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [studios, setStudios] = useState<any[]>([]);
  const [loadingClassTypes, setLoadingClassTypes] = useState(false);
  const [loadingStudios, setLoadingStudios] = useState(false);

  const [capabilityForm, setCapabilityForm] = useState({
    title: '',
    image: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Session type change on a pack members already hold: ask whether they move
  // to the new type too. Holds the payload until the studio picks.
  const [holderPrompt, setHolderPrompt] = useState<{
    typeChanged: boolean;
    from: string | null;
    to: string | null;
    // "Applies to" change (category / class / shared pool / combo), as labels.
    scope: { from: string; to: string; combo: boolean; comboMove: boolean } | null;
    activeMembers: number;
    bySessionType: Record<string, number>;
    payload: any;
    // allowGuestBooking also changed: ask about it next, in its own prompt.
    guestPending?: { to: boolean } | null;
  } | null>(null);
  // allowGuestBooking change on a pack members already hold: apply to them too,
  // or only to new purchases? Holds the payload until the studio picks.
  const [guestPrompt, setGuestPrompt] = useState<{
    to: boolean;
    activeMembers: number;
    payload: any;
  } | null>(null);
  // Errors render at the top of a long form, while Save is at the bottom —
  // scroll them into view so they are not missed.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [error]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCapabilityImage, setUploadingCapabilityImage] = useState(false);

  useEffect(() => {
    if (open) {
      if (pack) {
        const isStudioPack = !!pack.studio;
        setFormData({
          title: pack.title || '',
          image: pack.image || '',
          description: pack.description || '',
          totalClasses: pack.totalClasses?.toString() || '',
          totalPrice: pack.totalPrice?.toString() || pack.pricePerClass?.toString() || '', // Fallback to pricePerClass for backward compatibility
          capabilities: pack.capabilities || [],
          category: pack.category?._id || pack.category || '',
          packType: isStudioPack ? 'studio' : 'global',
          studio: pack.studio?._id || pack.studio || '',
          durationMonths: pack.durationMonths?.toString() || '',
          durationDays: pack.durationDays?.toString() || (pack.durationWeeks ? (pack.durationWeeks * 7).toString() : ''),
          sessionType: (pack.sessionType as 'group' | 'private' | 'duet') || 'group',
          freezeAllowed: pack.freezeAllowed !== false,
          isPrivate: pack.isPrivate === true,
          shareAcrossBrand: pack.shareAcrossBrand === true,
          allowGuestBooking: pack.allowGuestBooking === true,
        });
        const limit = Number(pack.maxPurchasesPerUser);
        setPurchaseLimitMode(Number.isInteger(limit) && limit >= 1 ? 'limited' : 'unlimited');
        setPurchaseLimitN(Number.isInteger(limit) && limit >= 1 ? String(limit) : '1');
        setFreezeDays(Number(pack.freezeDays) > 0 ? String(pack.freezeDays) : '');
        // A shared pack's lists win; otherwise fall back to the single field.
        const poolCats: string[] = (pack.allowedCategories ?? []).map(idOf).filter(Boolean);
        const poolClasses: string[] = (pack.allowedClasses ?? []).map(idOf).filter(Boolean);
        const packClassId = idOf(pack.class);
        const packCategoryId = idOf(pack.category);
        const cats = poolCats.length ? poolCats : packCategoryId ? [packCategoryId] : [];
        const clss = poolClasses.length ? poolClasses : packClassId ? [packClassId] : [];
        const isShared = poolCats.length > 0 || poolClasses.length > 0;
        setScopeCategories(isShared || clss.length ? [] : cats);
        setScopeClasses(isShared ? [] : clss);
        setNormalScope(isShared ? 'all' : clss.length ? 'class' : cats.length ? 'category' : 'all');
        const quotas = Array.isArray(pack.categoryQuotas) ? pack.categoryQuotas : [];
        const isClassCombo = quotas.some((q: any) => q.appliesTo === 'class');
        const sharedRows = poolClasses.length ? poolClasses : poolCats;
        setComboEnabled(quotas.length > 0 || sharedRows.length > 0);
        setComboCounting(sharedRows.length > 0 ? 'shared' : 'separate');
        setComboMode(isClassCombo || poolClasses.length > 0 ? 'class' : 'category');
        if (sharedRows.length > 0) {
          setComboQuotas(sharedRows.map((id) => ({ category: id, count: '' })));
        } else setComboQuotas(
          quotas.map((q: any) => ({
            category:
              q.appliesTo === 'wellness'
                // No category = the "any wellness" catch-all sentinel.
                ? (q.category?._id || q.category || 'wellness')
                : q.appliesTo === 'class'
                ? (q.class?._id || q.class || '')
                // A session bucket with no category is the catch-all
                // "any workout session" bucket.
                : (q.category?._id || q.category || 'workout'),
            count: q.count?.toString() || '',
          })),
        );
      } else {
        setFormData({
          title: '',
          image: '',
          description: '',
          totalClasses: '',
          totalPrice: '',
          capabilities: [],
          category: '',
          packType: isStudioRole || defaultStudioId ? 'studio' : 'global',
          studio: defaultStudioId || '',
          durationMonths: '',
          durationDays: '',
          sessionType: 'group',
          freezeAllowed: true,
          isPrivate: false,
          shareAcrossBrand: false,
          allowGuestBooking: false,
        });
        setPurchaseLimitMode('unlimited');
        setPurchaseLimitN('1');
        setFreezeDays('');
        setComboEnabled(false);
        setComboCounting('separate');
        setComboMode('category');
        setNormalScope('all');
        setComboQuotas([]);
        setScopeCategories([]);
        setScopeClasses([]);
      }
      setError(null);
      setCapabilityForm({ title: '', image: '' });
    }
  }, [open, pack, isStudioRole, defaultStudioId]);

  const fetchClassTypes = useCallback(async () => {
    try {
      setLoadingClassTypes(true);
      const response = await apiService.getClassTypes();
      setClassTypes(response.classTypes || []);
    } catch (err: any) {
      console.error('Error fetching class types:', err);
    } finally {
      setLoadingClassTypes(false);
    }
  }, []);

  // The studio this pack belongs to (studio role → own studio from JWT;
  // superadmin → the picked studio, which also covers edit mode since
  // formData.studio is prefilled from the pack).
  // ── WHICH BRANCH DOES A NEW PACK BELONG TO ──────────────────────────────
  //
  // Under "All locations" `admin.studio._id` is merely the branch the owner
  // signed in as, so a new pack landed there with nothing said. Editing reads
  // the location off the pack itself.
  const { locations, isAllLocations, roleByStudio, selectedLocationId } = useMyLocations();
  // Only where this account may create (manager or above); unfiltered when the
  // server did not send per-location roles.
  const creatableIds = locationsWithRole(roleByStudio, 'manager');
  const branchOptions = creatableIds
    ? locations.filter((l) => creatableIds.includes(String(l._id)))
    : locations;
  const [branchForNew, setBranchForNew] = useState('');

  // ── CREATE AT SEVERAL LOCATIONS ──────────────────────────────────────────
  // A brand with 2+ locations this account may create at ticks where the new
  // pack goes; each ticked location gets its own copy. The form (classes,
  // session types) is the FIRST ticked location's; a class picked there is
  // matched to the same class at each other location on save. Studios
  // without such a brand never see it.
  const studioPackForm = isStudioRole || formData.packType === 'studio';
  const atLocs = useCreateAtLocations(isStudioRole ? undefined : formData.studio, {
    enabled: open && !isEditMode && studioPackForm && (isStudioRole || !!formData.studio),
    initial: isStudioRole
      ? (selectedLocationId ? [selectedLocationId] : isAllLocations ? [] : [admin?.studio?._id || ''])
      : [formData.studio],
  });
  // "Usable at every location" only means something for a brand with more
  // than one location — a single studio never sees the switch (still shown
  // on a pack that already has it on, so it can be turned off).
  const { all: shareBrandLocs } = useBrandLocations(
    isStudioRole ? undefined : formData.studio,
    open && studioPackForm && (isStudioRole ? locations.length > 1 : !!formData.studio),
  );
  const showShareSwitch = shareBrandLocs.length > 1 || formData.shareAcrossBrand === true;
  // A super admin's picked studio is always one of them — it is the studio
  // the form is filled in for.
  const setAtLocs = (ids: string[]) =>
    atLocs.setSelected(!isStudioRole && formData.studio && !ids.includes(formData.studio) ? [formData.studio, ...ids] : ids);
  const mustPickBranch = isAllLocations && !isEditMode && isStudioRole && !atLocs.multi;

  const effectiveStudioId = isStudioRole
    ? (isEditMode
        ? (((pack as any)?.studio?._id ?? (pack as any)?.studio) || admin?.studio?._id || '')
        : atLocs.multi
          ? (atLocs.selected[0] || '')
          : (mustPickBranch ? branchForNew : (admin?.studio?._id || '')))
    : formData.studio;
  // A class picked from one location's list is not a class at another: when
  // the location the form is filled in for changes, those picks go.
  const formLocation = atLocs.multi ? atLocs.selected[0] || '' : '';
  const prevFormLocation = useRef(formLocation);
  useEffect(() => {
    if (prevFormLocation.current === formLocation) return;
    prevFormLocation.current = formLocation;
    if (isEditMode) return;
    setScopeClasses([]);
    if (comboMode === 'class') setComboQuotas([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formLocation]);
  // A fresh create starts with no location chosen (BranchSelect's rule).
  useEffect(() => {
    if (open) setBranchForNew('');
  }, [open]);

  // Classes for the selected studio (for the optional single-class restriction
  // and class-mode combo packs). Studio role gets their own studio's classes.
  const fetchClasses = useCallback(async () => {
    try {
      setLoadingClasses(true);
      let response: any;
      if (isStudioRole) {
        // Under "All locations" the class list must be the pack's own
        // location's — otherwise a pack at A could be restricted to a class
        // at B. Pinned to it; nothing loads until one is chosen.
        if ((isAllLocations || atLocs.multi) && !effectiveStudioId) {
          setClasses([]);
          return;
        }
        response = await apiService.getAllClasses(
          undefined,
          isAllLocations || atLocs.multi ? effectiveStudioId : undefined,
        );
      } else if (formData.studio) {
        response = await apiService.getClasses(formData.studio);
      } else {
        setClasses([]);
        return;
      }
      const list =
        response?.classes || response?.data?.classes || response?.data || [];
      setClasses(Array.isArray(list) ? list : []);
    } catch (err: any) {
      console.error('Error fetching classes:', err);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }, [isStudioRole, formData.studio, isAllLocations, effectiveStudioId, atLocs.multi]);

  const fetchStudios = useCallback(async () => {
    if (isStudioRole) return; // Studio/manager gets their studio from JWT server-side
    try {
      setLoadingStudios(true);
      // Fetch all studios - admin endpoint returns { success: true, data: [...], total: number }
      const response = await apiService.getStudios();
      // Handle different response structures
      const studiosData = response.data || response.studios || [];
      const studiosList = Array.isArray(studiosData) ? studiosData : [];
      setStudios(studiosList);
      console.log(`Loaded ${studiosList.length} studios`);
    } catch (err: any) {
      console.error('Error fetching studios:', err);
      setError('Failed to load studios. Please try again.');
      setStudios([]);
    } finally {
      setLoadingStudios(false);
    }
  }, [isStudioRole]);

  useEffect(() => {
    if (open) {
      fetchClassTypes();
      fetchStudios();
    }
  }, [open, fetchClassTypes, fetchStudios]);

  // (Re)load the studio's classes whenever the selected studio (or, under
  // "All locations", the chosen location) changes — fetchClasses' identity
  // follows both.
  useEffect(() => {
    if (open && (isStudioRole || formData.studio)) {
      fetchClasses();
    } else if (open) {
      setClasses([]);
    }
  }, [open, isStudioRole, formData.studio, fetchClasses]);


  // Session Type on a pack only means anything when the studio has
  // "Enable Session Types" on — otherwise there is no UI to set a session's
  // type (every session is 'group') and a pack saved as private/duet just
  // fails to pay for those sessions. Mirrors ClassFormDialog.
  const [resolvedEnableSessionTypes, setResolvedEnableSessionTypes] = useState(false);
  // Same for "Allow guest booking": it does nothing until the studio's own
  // guest booking switch (super admin) is on, so it stays hidden until then.
  const [studioGuestBookingOn, setStudioGuestBookingOn] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (!effectiveStudioId) {
      setResolvedEnableSessionTypes(false);
      setStudioGuestBookingOn(false);
      return;
    }
    let cancelled = false;
    apiService
      .getStudioById(effectiveStudioId)
      .then((res: any) => {
        const s = res.studio || res.data || res;
        if (!cancelled) {
          setResolvedEnableSessionTypes(!!s?.studioPermissions?.enableSessionTypes);
          setStudioGuestBookingOn(s?.studioPermissions?.enableGuestBooking === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedEnableSessionTypes(false);
          setStudioGuestBookingOn(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, effectiveStudioId]);
  const enableSessionTypes = resolvedEnableSessionTypes;
  // A pack already saved with it on keeps the switch visible, so it can be turned off.
  const loadedGuestBooking = (pack as any)?.allowGuestBooking === true;
  const showGuestBooking = studioGuestBookingOn || (isEditMode && loadedGuestBooking);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Categories a combo bucket can actually match.
   *
   * A category is stored against the dimension it belongs to: a wellness
   * ClassType writes appliesTo:'wellness', anything else writes 'session'. That
   * matters because matchComboBucket sends wellness bookings down a separate
   * branch that only looks at wellness buckets — a wellness ClassType stored as
   * a session bucket can never be consumed, so its credits sit unreachable while
   * the member is sent to checkout. That silently happened to 13 pack templates
   * in production, which is why the save path below normalises by studioType
   * rather than trusting which row the category was picked in.
   *
   * Event types stay excluded for a harder reason: a pack cannot pay for an
   * event at all — createManualBooking throws "Packs cannot be used for event
   * bookings". An event bucket is dead weight in every case.
   */
  const comboSelectableClassTypes = classTypes.filter(
    (ct: any) => ct?.studioType !== 'event',
  );
  // Group headers inside the combo dropdown. Plain ListSubheader text read as
  // just another (unselectable) option; these need to look like dividers, so
  // they get a tinted band, uppercase micro-type and a rule above.
  const comboGroupHeaderSx = {
    lineHeight: '30px',
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: '0.9px',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
    backgroundColor: 'action.hover',
    borderTop: '1px solid',
    borderColor: 'divider',
    '&:first-of-type': { borderTop: 'none' },
  };

  // The two catch-all rows close their group, so they sit slightly indented
  // with a rule above — the bucket they create is a fallback, not a peer of
  // the named categories.
  const comboCatchAllSx = {
    borderTop: '1px dashed',
    borderColor: 'divider',
    pl: 2.5,
    color: 'text.secondary',
  };

  const isWellnessType = (id: string) =>
    classTypes.find((ct: any) => ct._id === id)?.studioType === 'wellness';

  const comboTotal = comboQuotas.reduce((s, q) => s + (parseInt(q.count) || 0), 0);
  const addComboRow = () =>
    setComboQuotas((prev) => [...prev, { category: '', count: '' }]);
  const removeComboRow = (i: number) =>
    setComboQuotas((prev) => prev.filter((_, idx) => idx !== i));
  const updateComboRow = (i: number, field: 'category' | 'count', value: string) =>
    setComboQuotas((prev) =>
      prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
    );

  const handleFileUpload = async (file: File, isCapability: boolean = false) => {
    try {
      if (isCapability) {
        setUploadingCapabilityImage(true);
      } else {
        setUploadingImage(true);
      }
      
      const response = await apiService.uploadFiles([file], 'packs');
      console.log('Upload response:', response);
      const imageUrls = response.fileLinks || response.data?.fileLinks || [];
      const imageUrl = imageUrls[0] || response.url || response.data?.url;
      
      if (!imageUrl) {
        console.error('Upload response structure:', response);
        throw new Error('Failed to get image URL from upload response');
      }
      
      if (isCapability) {
        setCapabilityForm((prev) => ({ ...prev, image: imageUrl }));
      } else {
        handleInputChange('image', imageUrl);
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      if (isCapability) {
        setUploadingCapabilityImage(false);
      } else {
        setUploadingImage(false);
      }
    }
  };

  const handleAddCapability = () => {
    if (!capabilityForm.title || !capabilityForm.image) {
      setError('Please fill in both title and image for the capability');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      capabilities: [...prev.capabilities, { ...capabilityForm }],
    }));

    setCapabilityForm({ title: '', image: '' });
  };

  const handleRemoveCapability = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: prev.capabilities.filter((_, i) => i !== index),
    }));
  };

  const savePack = async (payload: any) => {
    // Pinned to the pack's OWN location so editing another branch's pack (or
    // creating at the picked one) works while "All locations" is selected.
    if (isEditMode) {
      const res: any = await apiService.updatePack(payload, effectiveStudioId);
      const change = res?.guestBookingChange ?? res?.data?.guestBookingChange;
      const skipped = (res?.scopeChange ?? res?.data?.scopeChange)?.skippedMembers;
      const notes = [
        change && guestBookingNote(change),
        skipped && `${skipped} member${skipped === 1 ? '' : 's'} not moved: upcoming bookings in a removed class.`,
      ].filter(Boolean);
      onSuccess(notes.length ? notes.join(' ') : undefined);
      return;
    } else if (atLocs.multi) {
      await savePackAtLocations(payload);
      return;
    } else {
      await apiService.createPack(payload, effectiveStudioId);
    }
    onSuccess();
  };

  const guestBookingNote = (c: { to: boolean; appliedToExisting: boolean; existingMembers: number }) => {
    const what = c.to ? 'Guest booking turned on' : 'Guest booking turned off';
    if (c.appliedToExisting) {
      return `${what} — applied to ${c.existingMembers} current member${c.existingMembers === 1 ? '' : 's'} too.`;
    }
    return c.existingMembers > 0 ? `${what} for new purchases. Current members keep what they had.` : `${what}.`;
  };

  /**
   * One copy per ticked location. The form's location gets the payload as
   * is; every other location gets its own classes in place of the form's
   * (same linked class, else same name) and is skipped, with the reason, when
   * it has no such class. Categories are shared, so they carry over as is.
   */
  const savePackAtLocations = async (payload: any) => {
    // The location the form's classes were loaded for: a super admin's picked
    // studio, else the first ticked location.
    const first = isStudioRole ? atLocs.selected[0] : formData.studio;
    const usedClassIds = [
      payload.class,
      ...(payload.allowedClasses ?? []),
      ...(payload.categoryQuotas ?? []).map((q: any) => q.class),
    ].filter(Boolean).map(String);
    const sourceOf = (id: string) => classes.find((c: any) => String(c._id) === id);

    const { created, failed } = await createAtEach(atLocs.selected, atLocs.nameOf, async (studioId) => {
      let body: any = { ...payload };
      if (!isStudioRole) body.studio = studioId;
      if (studioId !== first && usedClassIds.length) {
        const res: any = await apiService.getStudioClasses(studioId);
        const theirs = res?.classes || res?.data?.classes || res?.data || [];
        const map = (id: any) => {
          if (!id) return id;
          const m = matchClass(sourceOf(String(id)), theirs);
          if (!m) throw new Error(`no "${sourceOf(String(id))?.className ?? 'matching'}" class here`);
          return idOfLoc(m);
        };
        body = {
          ...body,
          class: map(body.class) ?? body.class,
          allowedClasses: (body.allowedClasses ?? []).map(map),
          categoryQuotas: (body.categoryQuotas ?? []).map((q: any) => (q.class ? { ...q, class: map(q.class) } : q)),
        };
      }
      await apiService.createPack(body, studioId);
    });

    if (!failed.length) {
      onSuccess();
      return;
    }
    // What was created stays; only the failed locations stay ticked, so a
    // retry does not create a second copy anywhere.
    atLocs.setSelected(failed.map((f) => f.studioId));
    setError(partialResultMessage(created, failed, atLocs.nameOf));
  };

  // "Applies to" of a template or of the form payload, normalised for comparing
  // and naming. Fields missing from the payload are unchanged from the pack.
  const scopeOf = (src: any, fallback?: any) => {
    const pick = (k: string) => (src && k in src ? src[k] : fallback?.[k]);
    const ids = (a: any) => (Array.isArray(a) ? a.map(idOf).filter(Boolean).sort() : []) as string[];
    const quotas = (Array.isArray(pick('categoryQuotas')) ? pick('categoryQuotas') : []).map((q: any) => ({
      appliesTo: q.appliesTo ?? 'session',
      category: q.category ? idOf(q.category) : '',
      class: q.class ? idOf(q.class) : '',
      count: q.count ?? null,
    }));
    return {
      category: pick('category') ? idOf(pick('category')) : '',
      class: pick('class') ? idOf(pick('class')) : '',
      allowedCategories: ids(pick('allowedCategories')),
      allowedClasses: ids(pick('allowedClasses')),
      quotas,
    };
  };
  const scopeKey = (sc: ReturnType<typeof scopeOf>) =>
    JSON.stringify({ ...sc, quotas: sc.quotas.map((q: any) => `${q.appliesTo}|${q.category}|${q.class}|${q.count}`).sort() });
  const categoryName = (id: string) =>
    classTypes.find((c: any) => c._id === id)?.title ||
    [pack?.category, ...(pack?.allowedCategories ?? []), ...(pack?.categoryQuotas ?? []).map((q: any) => q.category)]
      .find((c: any) => c && idOf(c) === id)?.title ||
    (id === 'wellness' ? 'Wellness' : id === 'workout' ? 'Workout' : 'a category');
  const className = (id: string) =>
    classes.find((c: any) => c._id === id)?.className ||
    [pack?.class, ...(pack?.allowedClasses ?? []), ...(pack?.categoryQuotas ?? []).map((q: any) => q.class)]
      .find((c: any) => c && idOf(c) === id)?.className ||
    'a class';
  const describeScope = (sc: ReturnType<typeof scopeOf>) => {
    if (sc.quotas.length) {
      return `Combo: ${sc.quotas
        .map((q: any) => `${q.class ? className(q.class) : q.category ? categoryName(q.category) : q.appliesTo === 'wellness' ? 'Any wellness' : 'Any class'} ${q.count ?? 'unlimited'}`)
        .join(', ')}`;
    }
    if (sc.allowedCategories.length) return sc.allowedCategories.map(categoryName).join(' + ');
    if (sc.allowedClasses.length) return sc.allowedClasses.map(className).join(' + ');
    if (sc.class) return `${className(sc.class)} class only`;
    if (sc.category) return `${categoryName(sc.category)} only`;
    return 'All classes';
  };

  const confirmHolderChoice = async (applyToHolders: boolean) => {
    if (!holderPrompt) return;
    // One answer for both: the backend never moves members between a normal
    // pack and a combo.
    const payload = {
      ...holderPrompt.payload,
      applySessionTypeToHolders: applyToHolders,
      applyScopeToHolders: applyToHolders,
    };
    const guestPending = holderPrompt.guestPending;
    setHolderPrompt(null);
    if (guestPending) {
      setGuestPrompt({ to: guestPending.to, activeMembers: holderPrompt.activeMembers, payload });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await savePack(payload);
    } catch (err: any) {
      setError(err.message || 'Failed to save pack');
    } finally {
      setLoading(false);
    }
  };

  const confirmGuestChoice = async (applyToHolders: boolean) => {
    if (!guestPrompt) return;
    const payload = { ...guestPrompt.payload, applyGuestBookingToHolders: applyToHolders };
    setGuestPrompt(null);
    try {
      setLoading(true);
      setError(null);
      await savePack(payload);
    } catch (err: any) {
      setError(err.message || 'Failed to save pack');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.title || !formData.description || !formData.totalPrice) {
        setError('Please fill in all required fields (Title, Description, Total Price)');
        return;
      }

      const payload: any = {
        title: formData.title,
        description: formData.description,
        totalPrice: parseFloat(formData.totalPrice),
        freezeAllowed: formData.freezeAllowed,
        isPrivate: formData.isPrivate,
        shareAcrossBrand: formData.shareAcrossBrand,
      };

      // Pack freeze days. null = studio freeze policy (and clears it on edit).
      if (freezeDays.trim() === '' || !formData.freezeAllowed) {
        payload.freezeDays = null;
      } else {
        const fd = parseInt(freezeDays, 10);
        if (!Number.isInteger(fd) || fd < 1) {
          setError('Freeze days must be a whole number of 1 or more, or left blank to use the studio freeze policy.');
          setLoading(false);
          return;
        }
        payload.freezeDays = fd;
      }

      // Purchases per member. null = unlimited (and clears a limit on edit).
      if (purchaseLimitMode === 'limited') {
        const n = parseInt(purchaseLimitN, 10);
        if (!Number.isInteger(n) || n < 1) {
          setError('Enter how many times a member can buy this pack (1 or more), or choose Unlimited.');
          setLoading(false);
          return;
        }
        payload.maxPurchasesPerUser = n;
      } else {
        payload.maxPurchasesPerUser = null;
      }

      if (formData.image) {
        payload.image = formData.image;
      }

      if (formData.capabilities && formData.capabilities.length > 0) {
        payload.capabilities = formData.capabilities;
      }

      if (formData.totalClasses) {
        payload.totalClasses = parseInt(formData.totalClasses);
      }

      // Set studio field based on pack type
      const effectivePackType = isStudioRole ? 'studio' : formData.packType;

      // Single scope of a normal (non-combo) pack: one category or one class.
      const pickedCategory = !comboEnabled && normalScope === 'category' ? scopeCategories[0] ?? '' : '';
      const pickedClass =
        !comboEnabled && normalScope === 'class' && effectivePackType === 'studio' ? scopeClasses[0] ?? '' : '';
      if (!comboEnabled && normalScope === 'category' && !pickedCategory) {
        setError('Choose a category, or set "Applies to" to All classes.');
        setLoading(false);
        return;
      }
      if (!comboEnabled && normalScope === 'class' && effectivePackType === 'studio' && !pickedClass) {
        setError('Choose a class, or set "Applies to" to All classes.');
        setLoading(false);
        return;
      }
      payload.category = pickedCategory || null;
      payload.class = pickedClass || null;
      // Shared pack lists are set from the combo rows below; any other pack clears them.
      payload.allowedCategories = [];
      payload.allowedClasses = [];
      if (effectivePackType === 'studio') {
        if (!isStudioRole && !formData.studio) {
          setError('Please select a studio for studio-specific pack');
          setLoading(false);
          return;
        }
        if (!isStudioRole) {
          payload.studio = formData.studio;
        }
        const months = formData.durationMonths ? parseInt(formData.durationMonths) : 0;
        const days = formData.durationDays ? parseInt(formData.durationDays) : 0;
        if (months === 0 && days === 0) {
          setError('Studio packs require at least one month or day duration');
          setLoading(false);
          return;
        }
        payload.durationMonths = months;
        payload.durationDays = days;
        payload.totalClasses = formData.totalClasses ? parseInt(formData.totalClasses) : null;
        // Only a session-types studio stores a real type; otherwise null =
        // all-access, so the pack is never rejected for "session type".
        payload.sessionType = enableSessionTypes ? formData.sessionType : null;
        if (canEditGuestBooking && showGuestBooking) payload.allowGuestBooking = formData.allowGuestBooking;

        // Shared pack (combo section, "One shared total"): the rows are the list
        // of categories / classes; Total Classes is the one count across them.
        if (comboShared) {
          const ids = Array.from(
            new Set(comboQuotas.map((q) => q.category).filter((c) => c && c !== 'workout' && c !== 'wellness')),
          );
          if (ids.length === 0) {
            setError(`Add at least one ${comboMode === 'class' ? 'class' : 'category'} for the shared pack.`);
            setLoading(false);
            return;
          }
          if (!(parseInt(formData.totalClasses) >= 1)) {
            setError(
              `Enter Total Classes — the number of classes members can use across the selected ${
                comboMode === 'class' ? 'classes' : 'categories'
              } in any mix.`,
            );
            setLoading(false);
            return;
          }
          payload.categoryQuotas = [];
          payload.category = null;
          payload.class = null;
          if (comboMode === 'class') payload.allowedClasses = ids;
          else payload.allowedCategories = ids;
          payload.totalClasses = parseInt(formData.totalClasses);
        } else if (comboEnabled) {
          // Combo pack (studio only): per-category quotas. The backend derives
          // totalClasses = Σ counts and spans categories (single category null).
          // A row needs a category/class (or 'wellness'). The count may be blank
          // = UNLIMITED for that bucket (sent as count: null), or a positive int.
          const quotas = comboQuotas
            .filter((q) => q.category && (q.count.trim() === '' || parseInt(q.count) > 0))
            .map((q) => {
              const count = q.count.trim() === '' ? null : parseInt(q.count);
              // A row can still hold a wellness ClassType id — an older pack loaded
              // for edit, before the backfill. Writing that as a session bucket
              // is what made those credits unreachable, so normalise here too
              // rather than relying only on the filtered dropdown.
              // A wellness ClassType always writes a WELLNESS bucket, whichever
              // row it was picked in; the 'wellness' sentinel is the catch-all
              // (category null = any wellness session).
              if (q.category === 'wellness') {
                return { appliesTo: 'wellness', category: null, count };
              }
              if (isWellnessType(q.category)) {
                return { appliesTo: 'wellness', category: q.category, count };
              }
              // "Any workout" — a session bucket with NO category. The matcher
              // only falls back to it when no specific category/class bucket
              // fits, so [Pilates 5, any-workout 20] still spends the Pilates
              // quota on a Pilates booking.
              if (q.category === 'workout') {
                return { appliesTo: 'session', category: null, count };
              }
              return comboMode === 'class'
                ? { appliesTo: 'class', class: q.category, count }
                : { appliesTo: 'session', category: q.category, count };
            });
          if (quotas.length === 0) {
            setError(
              `Add at least one ${comboMode === 'class' ? 'class' : 'category'} (count, or blank for unlimited) for a combo pack.`,
            );
            setLoading(false);
            return;
          }
          // One bucket per (dimension, target). Wellness rows are keyed by their
          // category, so Massage and Recovery can each hold their own quota; the
          // two catch-alls ('any workout' / 'any wellness') are singletons.
          const keys = quotas.map((q: any) =>
            q.appliesTo === 'class'
              ? `class:${q.class}`
              : `${q.appliesTo}:${q.category ?? '*'}`,
          );
          if (new Set(keys).size !== keys.length) {
            setError(
              keys.filter((k: any) => k === 'session:*').length > 1
                ? 'Only one "Workout (any workout session)" row is allowed — it already covers every workout category.'
                : keys.filter((k: any) => k === 'wellness:*').length > 1
                ? 'Only one "Wellness (any wellness session)" row is allowed — it already covers every wellness category.'
                : `Each ${comboMode === 'class' ? 'class' : 'category'} can appear only once in a combo pack.`,
            );
            setLoading(false);
            return;
          }
          payload.categoryQuotas = quotas;
          payload.category = null; // combo spans buckets
          payload.class = null;
          // Σ of finite buckets only — unlimited buckets (count null) contribute 0.
          payload.totalClasses = quotas.reduce(
            (s: number, q: any) => s + (q.count == null ? 0 : q.count),
            0,
          );
        } else if (isEditMode) {
          payload.categoryQuotas = []; // clear combo mode if it was on before
        }
      } else {
        payload.studio = null; // Global/SweatSocial pack
        // Duration is OPTIONAL for global packs — when both are 0 the backend
        // defaults the pack to 1 month validity (resolveGlobalPackDuration).
        payload.durationMonths = formData.durationMonths ? parseInt(formData.durationMonths) : 0;
        payload.durationDays = formData.durationDays ? parseInt(formData.durationDays) : 0;
        payload.totalClasses = formData.totalClasses ? parseInt(formData.totalClasses) : null; // Send null for unlimited
        // sessionType not applicable for global packs — leave null
      }

      if (isEditMode) {
        payload.packId = pack._id;
        // Send the session type only when the studio changed it. The form shows
        // an all-access (null) pack as Group, so re-sending it on every edit
        // would quietly turn all-access packs into Group-only ones.
        const loadedType = (pack.sessionType as string) || 'group';
        if ('sessionType' in payload && (!enableSessionTypes || formData.sessionType === loadedType)) {
          delete payload.sessionType;
        }
        const typeChanged = 'sessionType' in payload;
        const scopeBefore = scopeOf(pack);
        const scopeAfter = scopeOf(payload, pack);
        const scopeChanged = !!pack.studio && scopeKey(scopeBefore) !== scopeKey(scopeAfter);
        const guestChanged =
          'allowGuestBooking' in payload && payload.allowGuestBooking !== (pack.allowGuestBooking === true);
        if (!guestChanged) delete payload.allowGuestBooking;
        if (typeChanged || scopeChanged || guestChanged) {
          const holders = await apiService.getPackHolders(pack._id, effectiveStudioId);
          if (holders.activeMembers > 0 && !typeChanged && !scopeChanged) {
            setGuestPrompt({ to: payload.allowGuestBooking === true, activeMembers: holders.activeMembers, payload });
            return;
          }
          if (holders.activeMembers > 0) {
            setHolderPrompt({
              guestPending: guestChanged ? { to: payload.allowGuestBooking === true } : null,
              typeChanged,
              from: pack.sessionType ?? null,
              to: payload.sessionType ?? null,
              scope: scopeChanged
                ? {
                    from: describeScope(scopeBefore),
                    to: describeScope(scopeAfter),
                    combo: scopeBefore.quotas.length > 0 || scopeAfter.quotas.length > 0,
                    comboMove: scopeBefore.quotas.length > 0 && scopeAfter.quotas.length > 0,
                  }
                : null,
              activeMembers: holders.activeMembers,
              bySessionType: holders.bySessionType ?? {},
              payload,
            });
            return;
          }
        }
      } else {
        if (mustPickBranch && !branchForNew) {
          setError('Choose which location this pack belongs to');
          return;
        }
        if (atLocs.multi && !atLocs.selected.length) {
          setError('Choose at least one location for this pack');
          return;
        }
      }

      await savePack(payload);
    } catch (err: any) {
      setError(err.message || 'Failed to save pack');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Pack' : 'Create Pack'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert ref={errorRef} severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Where a brand with several locations creates the pack. */}
            {atLocs.multi && (
              <LocationChecks
                locations={atLocs.creatable}
                selected={atLocs.selected}
                onChange={setAtLocs}
                severalHint={
                  formData.shareAcrossBrand
                    ? 'Each location gets its own copy. This pack is also shared across the brand, so one copy may be enough.'
                    : `Each location gets its own copy. The classes below are ${atLocs.nameOf(atLocs.selected[0])}'s; the same class is used at the others.`
                }
              />
            )}

            {/* Location picker — a brand owner creating while viewing "All". */}
            {mustPickBranch && (
              <FormControl fullWidth size="small" required>
                <InputLabel>Location *</InputLabel>
                <Select
                  value={branchForNew}
                  label="Location *"
                  onChange={(e) => {
                    setBranchForNew(e.target.value);
                    // A class picked from the previous location's list is
                    // not a class at this one.
                    setScopeClasses([]);
                    if (comboMode === 'class') setComboQuotas([]);
                  }}
                >
                  {branchOptions.map((l) => (
                    <MenuItem key={l._id} value={l._id}>
                      {branchLabel(l)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isEditMode && !isStudioRole && !lockToStudio && (
              <FormControl component="fieldset">
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Pack Type *
                </Typography>
                <RadioGroup
                  row
                  value={formData.packType}
                  onChange={(e) => handleInputChange('packType', e.target.value)}
                >
                  <FormControlLabel
                    value="global"
                    control={<Radio />}
                    label="SweatSocial Pack (Valid for all studios)"
                  />
                  <FormControlLabel
                    value="studio"
                    control={<Radio />}
                    label="Studio-Specific Pack"
                  />
                </RadioGroup>
              </FormControl>
            )}

            {formData.packType === 'studio' && !isEditMode && !isStudioRole && !lockToStudio && (
              <Autocomplete
                fullWidth
                options={studios}
                getOptionLabel={(option) => option.studioName || ''}
                value={studios.find((s) => s._id === formData.studio) || null}
                onChange={(event, newValue) => {
                  handleInputChange('studio', newValue?._id || '');
                }}
                loading={loadingStudios}
                disabled={loadingStudios}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Studio *"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingStudios ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {option.studioName}
                      </Typography>
                      {option.city && (
                        <Typography variant="caption" color="text.secondary">
                          {option.city}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText="No studios found"
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option) =>
                    option.studioName?.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.city?.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            )}

            {lockToStudio && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Creating a studio-specific pack for:{' '}
                <strong>{defaultStudioName || 'Selected Studio'}</strong>
              </Alert>
            )}

            {isEditMode && pack?.studio && (
              <Alert severity="info" sx={{ mb: 1 }}>
                This is a studio-specific pack for: <strong>{pack.studio?.studioName || 'Selected Studio'}</strong>
              </Alert>
            )}

            {isEditMode && !pack?.studio && (
              <Alert severity="info" sx={{ mb: 1 }}>
                This is a SweatSocial pack (valid for all studios)
              </Alert>
            )}

            {/* Session Type — only for studio packs, and only when the studio
                actually uses session types (otherwise it just breaks pack
                booking, since every session is 'group'). */}
            {(formData.packType === 'studio' || isStudioRole) && enableSessionTypes && (
              <FormControl component="fieldset">
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  Session Type
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={1} display="block">
                  Users with this pack will only see sessions of this type.
                  {isEditMode && ' A change applies to new purchases. If members already hold this pack, you can choose whether it applies to them too.'}
                </Typography>
                <RadioGroup
                  row
                  value={formData.sessionType}
                  onChange={(e) => handleInputChange('sessionType', e.target.value)}
                >
                  <FormControlLabel value="group" control={<Radio />} label="Group" />
                  <FormControlLabel value="private" control={<Radio />} label="Private" />
                  <FormControlLabel value="duet" control={<Radio />} label="Duet" />
                </RadioGroup>
              </FormControl>
            )}

            {/* Freeze eligibility — only relevant for studio packs */}
            {(formData.packType === 'studio' || isStudioRole) && (
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.freezeAllowed}
                      onChange={(e) => handleInputChange('freezeAllowed', e.target.checked)}
                    />
                  }
                  label="Allow pack freeze"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  When off, users cannot freeze a pack bought from this template (e.g. small 4-class packs). Subject to the studio's freeze policy.
                </Typography>
                {formData.freezeAllowed && (
                  <Box sx={{ mt: 1.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Freeze days for this pack"
                      placeholder="Studio policy"
                      value={freezeDays}
                      onChange={(e) => setFreezeDays(e.target.value)}
                      onWheel={stopWheelNudge}
                      inputProps={{ min: 1 }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 220 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {freezeDays.trim()
                        ? `Members get ${freezeDays} freeze days in total for the life of this pack (no monthly reset, no carry-over), even if studio-wide freeze is off. Applies to packs bought or assigned from now on.`
                        : "Leave blank to follow the studio's freeze policy."}
                    </Typography>
                  </Box>
                )}
              </FormControl>
            )}

            {/* Assign-only (private) pack — studio packs only */}
            {(formData.packType === 'studio' || isStudioRole) && (
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPrivate}
                      onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                    />
                  }
                  label="Private (assign-only)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  When on, this pack is hidden from the app and cannot be bought. Grant it to specific users with “Assign Pack” (payment collected offline).
                </Typography>
              </FormControl>
            )}

            {/* Guest booking — studio packs only, and only once the studio's guest booking switch is on */}
            {(formData.packType === 'studio' || isStudioRole) && showGuestBooking && (
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowGuestBooking}
                      disabled={!canEditGuestBooking}
                      onChange={(e) => handleInputChange('allowGuestBooking', e.target.checked)}
                    />
                  }
                  label="Allow guest booking"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {studioGuestBookingOn
                    ? 'Holders can book friends onto a class; each friend uses one credit.'
                    : 'Guest booking is off for this studio, so this has no effect. You can still turn it off here.'}
                </Typography>
              </FormControl>
            )}

            {/* Usable at every location of the brand — decided per pack */}
            {(formData.packType === 'studio' || isStudioRole) && showShareSwitch && (
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.shareAcrossBrand}
                      onChange={(e) => handleInputChange('shareAcrossBrand', e.target.checked)}
                    />
                  }
                  label="Usable at every location"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Members who buy this pack can book with it at all of this brand’s locations, not just where they bought it — e.g. a founding-member pack. Only matters for a brand with more than one location. Turning it off later does not affect members who already bought it.
                </Typography>
              </FormControl>
            )}

            {/* Purchases per member — studio and SweatSocial packs */}
            <FormControl component="fieldset">
              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                Purchases per member
              </Typography>
              <RadioGroup
                row
                value={purchaseLimitMode}
                onChange={(e) => setPurchaseLimitMode(e.target.value as 'unlimited' | 'limited')}
              >
                <FormControlLabel value="unlimited" control={<Radio size="small" />} label="Unlimited" />
                <FormControlLabel value="limited" control={<Radio size="small" />} label="Up to" />
                {purchaseLimitMode === 'limited' && (
                  <TextField
                    size="small"
                    type="number"
                    value={purchaseLimitN}
                    onChange={(e) => setPurchaseLimitN(e.target.value)}
                    onWheel={stopWheelNudge}
                    inputProps={{ min: 1 }}
                    sx={{ width: 90, ml: -1 }}
                    InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">times</Typography> }}
                  />
                )}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" display="block">
                {purchaseLimitMode === 'unlimited'
                  ? 'Members can buy this pack any number of times.'
                  : `A member can buy this pack ${purchaseLimitN === '1' ? 'only once' : `up to ${purchaseLimitN || 'N'} times`}. Once they reach it, the pack no longer shows for them in the app. Past copies count, including expired ones.`}
              </Typography>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              required
            />

            {/* Scope (Applies to) + Category/Class fields moved BELOW the combo toggle */}

            {/* Combo pack — studio packs only */}
            {(formData.packType === 'studio' || isStudioRole) && (
              <Box sx={{ border: `1px solid ${colors.divider}`, borderRadius: 1, p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={comboEnabled}
                      onChange={(e) => setComboEnabled(e.target.checked)}
                    />
                  }
                  label="Combo pack (split classes across categories or classes)"
                />
                <Typography variant="caption" color="text.secondary" display="block" mb={comboEnabled ? 1.5 : 0}>
                  {comboShared
                    ? `Members can use Total Classes on any of the selected ${comboMode === 'class' ? 'classes' : 'categories'}, in any mix (e.g. 19 Pilates + 1 Yoga out of 20).`
                    : <>Assign how many classes can be used per {comboMode === 'class' ? 'class' : 'category'}
                      {comboMode === 'class' ? ' (e.g. Morning Flow 5, Power Flow 5)' : ' (e.g. Pilates 5, Yoga 5)'}.
                      Total Classes = the sum.</>}
                  {' '}A pack covers categories OR classes, not both.
                </Typography>
                {comboEnabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <FormControl component="fieldset">
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Split by
                      </Typography>
                      <RadioGroup
                        row
                        value={comboMode}
                        onChange={(e) => {
                          setComboMode(e.target.value as 'category' | 'class');
                          setComboQuotas([]); // refs differ between modes — start fresh
                        }}
                      >
                        <FormControlLabel value="category" control={<Radio size="small" />} label="Category" />
                        <FormControlLabel value="class" control={<Radio size="small" />} label="Specific class" />
                      </RadioGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        How classes are counted
                      </Typography>
                      <RadioGroup
                        row
                        value={comboCounting}
                        onChange={(e) => setComboCounting(e.target.value as 'separate' | 'shared')}
                      >
                        <FormControlLabel
                          value="separate"
                          control={<Radio size="small" />}
                          label={`Separate count per ${comboMode === 'class' ? 'class' : 'category'}`}
                        />
                        <FormControlLabel
                          value="shared"
                          control={<Radio size="small" />}
                          label="One shared total"
                        />
                      </RadioGroup>
                    </FormControl>
                    {comboQuotas.map((row, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl sx={{ flex: 2 }} size="small">
                          <InputLabel>{comboMode === 'class' ? 'Class' : 'Category'}</InputLabel>
                          <Select
                            value={row.category}
                            label={comboMode === 'class' ? 'Class' : 'Category'}
                            onChange={(e) => updateComboRow(i, 'category', e.target.value)}
                            disabled={comboMode === 'class' ? loadingClasses : loadingClassTypes}
                          >
                            {comboMode === 'class'
                              ? classes.map((cls) => (
                                  <MenuItem key={cls._id} value={cls._id}>
                                    {cls.className}
                                  </MenuItem>
                                ))
                              : /* Split so it is obvious which side of the pack a
                                   category draws from — the two dimensions never
                                   pay for each other. Event types are absent; a
                                   pack can never pay for an event. */
                                [
                                  ...(comboSelectableClassTypes.some(
                                    (ct: any) => ct.studioType !== 'wellness',
                                  )
                                    ? [
                                        <ListSubheader
                                          key="h-workout"
                                          sx={comboGroupHeaderSx}
                                        >
                                          Workout categories
                                        </ListSubheader>,
                                      ]
                                    : []),
                                  ...comboSelectableClassTypes
                                    .filter((ct: any) => ct.studioType !== 'wellness')
                                    .map((ct) => (
                                      <MenuItem key={ct._id} value={ct._id}>
                                        {ct.title}
                                      </MenuItem>
                                    )),
                                  ...(comboShared ? [] : [
                                  <MenuItem
                                    key="workout"
                                    value="workout"
                                    sx={comboCatchAllSx}
                                  >
                                    <em>Any workout session</em>
                                  </MenuItem>]),
                                  ...(comboSelectableClassTypes.some(
                                    (ct: any) => ct.studioType === 'wellness',
                                  )
                                    ? [
                                        <ListSubheader
                                          key="h-wellness"
                                          sx={{
                                            ...comboGroupHeaderSx,
                                            color: colors.info,
                                            backgroundColor: `${colors.info}14`,
                                            borderTop: `1px solid ${colors.info}33`,
                                          }}
                                        >
                                          Wellness categories
                                        </ListSubheader>,
                                      ]
                                    : []),
                                  ...comboSelectableClassTypes
                                    .filter((ct: any) => ct.studioType === 'wellness')
                                    .map((ct) => (
                                      <MenuItem key={ct._id} value={ct._id}>
                                        {ct.title}
                                      </MenuItem>
                                    )),
                                  ...(comboShared ? [] : [
                                  <MenuItem
                                    key="wellness"
                                    value="wellness"
                                    sx={comboCatchAllSx}
                                  >
                                    <em>Any wellness session</em>
                                  </MenuItem>]),
                                ]}
                            {comboMode === 'class' && !comboShared && [
                              <MenuItem key="wellness" value="wellness">
                                <em>Any wellness session</em>
                              </MenuItem>,
                            ]}
                          </Select>
                        </FormControl>
                        {!comboShared && (
                        <TextField
                          size="small"
                          label="Classes"
                          type="number"
                          value={row.count}
                          onChange={(e) => updateComboRow(i, 'count', e.target.value)}
                          onWheel={stopWheelNudge}
                          placeholder="Unlimited"
                          helperText={row.count.trim() === '' ? 'Unlimited' : ' '}
                          sx={{ flex: 1 }}
                        />
                        )}
                        <IconButton color="error" size="small" onClick={() => removeComboRow(i)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Button size="small" startIcon={<Add />} onClick={addComboRow}>
                        Add {comboMode === 'class' ? 'class' : 'category'}
                      </Button>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {comboShared
                          ? `Shared total: ${formData.totalClasses || '—'} (set in Total Classes below)`
                          : `Total: ${comboTotal} finite${comboQuotas.some((q) => q.category && q.count.trim() === '') ? ' + unlimited' : ''}`}
                      </Typography>
                    </Box>
                    {!comboShared && (
                    <Typography variant="caption" color="text.secondary">
                      Leave a row's <strong>Classes</strong> blank for <strong>unlimited</strong> access to that category / wellness.
                      A specific category is always spent first; "Any workout" and
                      "Any wellness" only cover what no other row does, and never
                      cover each other.
                    </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Non-combo scope: applies everywhere, OR one category, OR one class */}
            {!comboEnabled && (
              <>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                    Applies to
                  </Typography>
                  <RadioGroup
                    row
                    value={normalScope}
                    onChange={(e) =>
                      applyScope(e.target.value as 'all' | 'category' | 'class')
                    }
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All classes"
                    />
                    <FormControlLabel
                      value="category"
                      control={<Radio />}
                      label="Category"
                    />
                    {(formData.packType === 'studio' || isStudioRole) && (
                      <FormControlLabel
                        value="class"
                        control={<Radio />}
                        label="Specific class"
                      />
                    )}
                  </RadioGroup>
                </FormControl>
                {normalScope === 'category' && (
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={scopeCategories[0] ?? ''}
                      label="Category"
                      onChange={(e) => setScopeCategories(e.target.value ? [e.target.value as string] : [])}
                      disabled={loadingClassTypes}
                    >
                      {classTypes.map((classType) => (
                        <MenuItem key={classType._id} value={classType._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <span>{classType.title}</span>
                            {/* Which kind of category this is. Without it the list is
                                just names — "Recovery", "Therapy" and "Fitness-Event"
                                look identical to "Pilates", yet they behave completely
                                differently: wellness sessions draw on a pack's wellness
                                allowance, and packs cannot pay for events at all. */}
                            {classType.studioType === 'wellness' && (
                              <Chip size="small" label="Wellness"
                                sx={{ height: 18, fontSize: 10, backgroundColor: `${colors.info}1F`, color: colors.info }} />
                            )}
                            {classType.studioType === 'event' && (
                              <Chip size="small" label="Event"
                                sx={{ height: 18, fontSize: 10, backgroundColor: `${colors.warning}22`, color: '#8a5a00' }} />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {normalScope === 'class' &&
                  (formData.packType === 'studio' || isStudioRole) && (
                    <FormControl fullWidth>
                      <InputLabel>Class</InputLabel>
                      <Select
                        value={scopeClasses[0] ?? ''}
                        label="Class"
                        onChange={(e) => setScopeClasses(e.target.value ? [e.target.value as string] : [])}
                        disabled={
                          loadingClasses || (!isStudioRole && !formData.studio)
                        }
                      >
                        {classes.map((cls) => (
                          <MenuItem key={cls._id} value={cls._id}>
                            {cls.className}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                {normalScope === 'all' && (
                  <Typography variant="caption" color="text.secondary">
                    This pack can be used for any class at the studio.
                  </Typography>
                )}
              </>
            )}

            {(() => {
              const isGlobalPack = formData.packType === 'global' && !isStudioRole;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      fullWidth
                      label="Duration (Months)"
                      type="number"
                      value={formData.durationMonths}
                      onChange={(e) => handleInputChange('durationMonths', e.target.value)}
                      onWheel={stopWheelNudge}
                      helperText={isGlobalPack ? 'Optional — defaults to 1 month if left 0' : 'Set to 0 if not applicable'}
                      sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
                    />
                    <TextField
                      fullWidth
                      label="Duration (Days)"
                      type="number"
                      value={formData.durationDays}
                      onChange={(e) => handleInputChange('durationDays', e.target.value)}
                      onWheel={stopWheelNudge}
                      helperText={isGlobalPack ? 'Optional — added on top of months' : 'Set to 0 if not applicable'}
                      sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      fullWidth
                      label="Total Classes"
                      type="number"
                      value={comboEnabled && !comboShared ? String(comboTotal) : formData.totalClasses}
                      onChange={(e) => handleInputChange('totalClasses', e.target.value)}
                      onWheel={stopWheelNudge}
                      disabled={comboEnabled && !comboShared}
                      helperText={
                        comboShared
                          ? 'Required — shared across the selected ' + (comboMode === 'class' ? 'classes' : 'categories')
                          : comboEnabled
                          ? 'Auto — sum of the combo categories above'
                          : formData.totalClasses
                          ? ''
                          : 'Leave empty for unlimited classes'
                      }
                      required={isSharedScope}
                      placeholder={isSharedScope ? 'e.g. 20' : 'Unlimited'}
                      sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
                    />
                    <TextField
                      fullWidth
                      label="Total Price"
                      type="number"
                      value={formData.totalPrice}
                      onChange={(e) => handleInputChange('totalPrice', e.target.value)}
                      onWheel={stopWheelNudge}
                      required
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                      }}
                      sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
                    />
                  </Box>
                </Box>
              );
            })()}

            <Box>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Pack Image (Optional)
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="pack-image-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, false);
                    }}
                  />
                  <label htmlFor="pack-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={uploadingImage}
                      startIcon={uploadingImage && <CircularProgress size={16} />}
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </label>
                  {formData.image && (
                    <Box
                      component="img"
                      src={formData.image}
                      alt="Pack"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: `1px solid ${colors.divider}`,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Capabilities (Optional)
              </Typography>
              <Box mb={2} p={2} sx={{ border: `1px solid ${colors.divider}`, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Capability Title"
                    value={capabilityForm.title}
                    onChange={(e) =>
                      setCapabilityForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}
                  />
                  <Box display="flex" gap={1} alignItems="center" sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="capability-image-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, true);
                      }}
                    />
                    <label htmlFor="capability-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        disabled={uploadingCapabilityImage}
                        startIcon={uploadingCapabilityImage && <CircularProgress size={16} />}
                      >
                        {uploadingCapabilityImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                    </label>
                    {capabilityForm.image && (
                      <Box
                        component="img"
                        src={capabilityForm.image}
                        alt="Capability"
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddCapability}
                    size="small"
                    disabled={!capabilityForm.title || !capabilityForm.image}
                    sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}
                  >
                    Add Capability
                  </Button>
                </Box>
              </Box>

              {formData.capabilities.length > 0 && (
                <Box display="flex" flexDirection="column" gap={1}>
                  {formData.capabilities.map((capability, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" gap={2} alignItems="center">
                            {capability.image && (
                              <CardMedia
                                component="img"
                                image={capability.image}
                                alt={capability.title}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                }}
                              />
                            )}
                            <Typography variant="body1" fontWeight={600}>
                              {capability.title}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveCapability(index)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            backgroundColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>

      <Dialog open={!!holderPrompt} onClose={() => setHolderPrompt(null)} maxWidth="sm" fullWidth>
        {holderPrompt && (() => {
          const from = sessionTypeLabel(holderPrompt.from);
          const to = sessionTypeLabel(holderPrompt.to);
          const n = holderPrompt.activeMembers;
          const members = `${n} member${n === 1 ? '' : 's'}`;
          const mixed = Object.keys(holderPrompt.bySessionType).length > 1;
          const scope = holderPrompt.scope;
          const typeOnly = !scope;
          // Combo to combo can move. Between a normal pack and a combo it cannot;
          // only a session type change (if any) still can.
          const scopeMoves = !!scope && (!scope.combo || scope.comboMove);
          const canMove = scopeMoves || holderPrompt.typeChanged;
          const moveWhat = [
            holderPrompt.typeChanged && `${to} sessions`,
            scopeMoves && scope!.to,
          ].filter(Boolean).join(' and ');
          return (
            <>
              <DialogTitle>
                {typeOnly ? 'Apply the new session type to current members?' : 'Apply these changes to current members?'}
              </DialogTitle>
              <DialogContent>
                {typeOnly ? (
                  <Typography variant="body2" mb={1.5}>
                    You are changing <strong>{pack?.title}</strong> from <strong>{from}</strong> to <strong>{to}</strong>.
                  </Typography>
                ) : (
                  <Box mb={1.5}>
                    <Typography variant="body2" mb={0.5}>
                      You are changing <strong>{pack?.title}</strong>:
                    </Typography>
                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.75, py: 1 }}>
                      {holderPrompt.typeChanged && (
                        <Typography variant="body2">
                          Session type: {from} → <strong>{to}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Applies to: {scope!.from} → <strong>{scope!.to}</strong>
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Typography variant="body2" mb={mixed ? 1 : 2}>
                  <strong>{members}</strong> already {n === 1 ? 'holds' : 'hold'} this pack and can still use it.
                  What should happen to them?
                </Typography>
                {mixed && (
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Right now:{' '}
                    {Object.entries(holderPrompt.bySessionType)
                      .map(([t, c]) => `${c} on ${sessionTypeLabel(t === 'all' ? null : t)}`)
                      .join(', ')}
                  </Typography>
                )}
                {scope?.combo && !scope.comboMove && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Combo changes apply to new purchases only. Current members keep the classes and session
                    counts they bought.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => confirmHolderChoice(false)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>
                        {typeOnly ? 'Keep their current session type (recommended)' : 'Keep what their pack covers today (recommended)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {typeOnly
                          ? `They keep booking ${mixed ? 'the sessions their pack allows today' : `${from} sessions`} until their pack ends. Only new purchases get ${to}.`
                          : 'They keep booking the classes their pack allows today until it ends. Only new purchases get the changes.'}
                      </Typography>
                    </Box>
                  </Button>
                  {canMove && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => confirmHolderChoice(true)}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {typeOnly ? `Move ${members} to ${to}` : `Move ${members} to the new ${scopeMoves ? 'classes' : 'session type'}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {typeOnly
                            ? `From now on they can book only ${to} sessions with this pack.`
                            : `From now on their pack covers ${moveWhat}.`}{' '}
                          {scope?.comboMove
                            ? "Sessions they've used still count, so remaining sessions change if counts change. Expiry date stays the same. Members with upcoming bookings in a removed class keep their current pack."
                            : 'Their remaining sessions and expiry date stay the same. Bookings they already made are not changed.'}
                        </Typography>
                      </Box>
                    </Button>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHolderPrompt(null)}>Cancel</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Dialog open={!!guestPrompt} onClose={() => setGuestPrompt(null)} maxWidth="sm" fullWidth>
        {guestPrompt && (() => {
          const n = guestPrompt.activeMembers;
          const members = `${n} member${n === 1 ? '' : 's'}`;
          const onOff = guestPrompt.to ? 'on' : 'off';
          return (
            <>
              <DialogTitle>Apply the guest booking change to current members?</DialogTitle>
              <DialogContent>
                <Typography variant="body2" mb={1.5}>
                  You are turning guest booking <strong>{onOff}</strong> for <strong>{pack?.title}</strong>.
                </Typography>
                <Typography variant="body2" mb={2}>
                  <strong>{members}</strong> {n === 1 ? 'holds' : 'hold'} this pack. Apply to them too, or only to new
                  purchases?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => confirmGuestChoice(false)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>Only new purchases (recommended)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Current members keep what their pack allows today until it ends.
                      </Typography>
                    </Box>
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => confirmGuestChoice(true)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', py: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>Apply to {members} too</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {guestPrompt.to
                          ? 'From now on they can book friends onto a class with this pack.'
                          : 'From now on they can no longer book friends with this pack. Guest bookings already made are not changed.'}
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setGuestPrompt(null)}>Cancel</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Dialog>
  );
};

export default PackFormDialog;

